import dec from './library'

/**
 * Redondeo COP a múltiplos de 100:
 * resto 0–50 → abajo; 51–99 → arriba.
 * Ej: 6750 → 6700, 6751 → 6800, 6749 → 6700.
 */
export function roundFareToHundred(value) {
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n === 0) return 0;
  const sign = n < 0 ? -1 : 1;
  const abs = Math.abs(n);
  const base = Math.floor(abs / 100) * 100;
  const rem = abs - base;
  if (rem <= 50) return sign * base;
  return sign * (base + 100);
}

/**
 * Safe fare estimate. Never throws — callers get zeros + error flag instead of a white screen.
 */
export const FareCalculator = (distance, time, rateDetails, instructionData, decimal) => {
  try {
    if (!rateDetails || typeof rateDetails !== 'object') {
      return { totalCost: 0, grandTotal: 0, convenience_fees: 0, error: 'missing_rate_details' };
    }

    const dist = parseFloat(distance) || 0;
    const secs = parseFloat(time) || 0;
    const rateDist = parseFloat(rateDetails.rate_per_unit_distance) || 0;
    // Tiempo (T) en minutos: rate_per_minute preferido; rate_per_hour se interpreta
    // como COP/minuto tras migración (el nombre del campo se mantiene por compat RTDB).
    const ratePerMinute =
      parseFloat(rateDetails.rate_per_minute) ||
      parseFloat(rateDetails.rate_per_hour) ||
      0;
    const minFare = parseFloat(rateDetails.min_fare) || 0;
    // Km incluidos en la mínima (ej. Barranquilla 2.5). Si no hay valor, se usa el piso clásico.
    const minFareDistance = parseFloat(
      rateDetails.min_fare_distance ?? rateDetails.included_distance ?? 0
    ) || 0;
    const convFees = parseFloat(rateDetails.convenience_fees) || 0;
    const hasIncludedKm = minFareDistance > 0 && minFare > 0;

    // Con min_fare_distance: la mínima cubre los primeros X km; el resto va a tarifa/km.
    // Sin ese campo: distancia completa × COP/km y luego piso con min_fare.
    const distanceCost = hasIncludedKm
      ? minFare + Math.max(0, dist - minFareDistance) * rateDist
      : rateDist * dist;
    const timeCost = ratePerMinute * (secs / 60);

    let baseCalculated = distanceCost + timeCost;
    if (rateDetails.base_fare > 0) {
      baseCalculated = baseCalculated + parseFloat(rateDetails.base_fare);
    }
    if (instructionData && instructionData.parcelTypeSelected) {
      baseCalculated = baseCalculated + (parseFloat(instructionData.parcelTypeSelected.amount) || 0);
    }
    if (instructionData && instructionData.optionSelected) {
      baseCalculated = baseCalculated + (parseFloat(instructionData.optionSelected.amount) || 0);
    }
    if (instructionData && instructionData.roundTrip) {
      baseCalculated = baseCalculated * 2;
    }
    let total = hasIncludedKm
      ? Math.max(baseCalculated, minFare)
      : (baseCalculated > minFare ? baseCalculated : minFare);
    // Tarifa base a múltiplo de 100 antes de comisión (montos mostrados en COP).
    total = roundFareToHundred(total);
    let convenienceFee = 0;
    if (rateDetails.convenience_fee_type && rateDetails.convenience_fee_type == 'flat') {
      convenienceFee = convFees;
    } else {
      convenienceFee = (total * convFees) / 100;
    }
    let grand = roundFareToHundred(total + convenienceFee);
    convenienceFee = Math.max(0, grand - total);

    const safeDec = (n) => {
      try {
        return parseFloat(dec(n));
      } catch (e) {
        return Math.round((parseFloat(n) || 0) * 100) / 100;
      }
    };

    return {
      totalCost: safeDec(total),
      grandTotal: safeDec(grand),
      convenience_fees: safeDec(convenienceFee),
    };
  } catch (error) {
    console.error('[FareCalculator] Error:', error?.message || error);
    return {
      totalCost: 0,
      grandTotal: 0,
      convenience_fees: 0,
      error: error?.message || 'fare_calc_failed',
    };
  }
};
